import { useState, useEffect, useCallback } from 'react';
import { useServices } from '@/app/providers/ServiceContext';
import { useConfirmation } from '@/app/providers';
import { useAbility } from '@/modules/identity/application/ability';
import type { NotificationCategory, NotificationCadence, QuietHours } from '@yawi/types';
import type {
  NotificationConfig,
  NotificationType,
  NotificationChannel,
  NotificationPriority,
} from '@yawi/domain';
import { CATEGORY_TO_TYPES } from '@yawi/domain';
import { BarChart3, Calendar, CreditCard, FileText, Key, Lock, Megaphone, PawPrint, Ticket, Users, type LucideIcon } from 'lucide-react';
import { Switch } from '@/presentation/components/atoms/Switch';

// ---------------------------------------------------------------------------
// Metadata
// ---------------------------------------------------------------------------

const CATEGORY_META: Record<NotificationCategory, { label: string; icon: LucideIcon }> = {
  reservations: { label: 'Reservas', icon: Calendar },
  payments: { label: 'Pagos', icon: CreditCard },
  tickets: { label: 'Soporte', icon: Ticket },
  announcements: { label: 'Anuncios', icon: Megaphone },
  access: { label: 'Control de acceso', icon: Key },
  guests: { label: 'Invitados', icon: Users },
  pets: { label: 'Mascotas', icon: PawPrint },
  documents: { label: 'Documentos', icon: FileText },
  polls: { label: 'Encuestas', icon: BarChart3 },
  security: { label: 'Seguridad', icon: Lock },
};

const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  PUSH: 'Push', EMAIL: 'Email', SMS: 'SMS', IN_APP: 'App', WHATSAPP: 'WhatsApp',
};

const PRIORITY_LABELS: Record<NotificationPriority, string> = {
  LOW: 'Baja', NORMAL: 'Normal', HIGH: 'Alta', URGENT: 'Urgente',
};

const DISPLAY_CHANNELS: NotificationChannel[] = ['PUSH', 'EMAIL', 'SMS', 'IN_APP'];

const DAYS_OF_WEEK = [
  { value: 0 as const, label: 'Domingo' }, { value: 1 as const, label: 'Lunes' },
  { value: 2 as const, label: 'Martes' }, { value: 3 as const, label: 'Miércoles' },
  { value: 4 as const, label: 'Jueves' }, { value: 5 as const, label: 'Viernes' },
  { value: 6 as const, label: 'Sábado' },
];

const TYPE_LABELS: Record<NotificationType, string> = {
  USER_WELCOME: 'Bienvenida de usuario',
  USER_BLOCKED: 'Usuario bloqueado',
  USER_UNBLOCKED: 'Usuario desbloqueado',
  PASSWORD_CHANGED: 'Contraseña cambiada',
  NEW_DEVICE_LOGIN: 'Inicio de sesión en nuevo dispositivo',
  GUEST_ARRIVED: 'Invitado llegó',
  GUEST_DELETED: 'Invitado eliminado',
  ACCESS_DENIED_BLACKLIST: 'Acceso denegado (lista negra)',
  DEVICE_OFFLINE: 'Dispositivo desconectado',
  DEVICE_ONLINE: 'Dispositivo conectado',
  INVITATION_CREATED: 'Invitación creada',
  INVITATION_EXPIRING: 'Invitación por vencer',
  INVITATION_USED: 'Invitación usada',
  RESERVATION_CREATED: 'Reserva creada',
  RESERVATION_CONFIRMED: 'Reserva confirmada',
  RESERVATION_CANCELLED: 'Reserva cancelada',
  RESERVATION_REMINDER_24H: 'Recordatorio de reserva (24h)',
  RESERVATION_REMINDER_2H: 'Recordatorio de reserva (2h)',
  DEPOSIT_REFUNDED: 'Depósito reembolsado',
  DEPOSIT_DEDUCTED: 'Depósito descontado',
  PAYMENT_CREATED: 'Pago creado',
  PAYMENT_REMINDER_5D: 'Recordatorio de pago (5 días)',
  PAYMENT_REMINDER_DUE: 'Recordatorio de pago (vencimiento)',
  PAYMENT_OVERDUE: 'Pago vencido',
  PROOF_RECEIVED: 'Comprobante recibido',
  PAYMENT_VERIFIED: 'Pago verificado',
  PAYMENT_REJECTED: 'Pago rechazado',
  INVOICE_GENERATED: 'Factura generada',
  TICKET_CREATED: 'Ticket creado',
  TICKET_ASSIGNED: 'Ticket asignado',
  TICKET_STATUS_CHANGED: 'Ticket: cambio de estado',
  TICKET_COMMENT_ADMIN: 'Comentario de administrador en ticket',
  TICKET_COMMENT_USER: 'Comentario de usuario en ticket',
  TICKET_RESOLVED: 'Ticket resuelto',
  TICKET_REOPENED: 'Ticket reabierto',
  TICKET_SLA_WARNING: 'Advertencia de SLA en ticket',
  ANNOUNCEMENT_INFO: 'Comunicado informativo',
  ANNOUNCEMENT_IMPORTANT: 'Comunicado importante',
  ANNOUNCEMENT_URGENT: 'Comunicado urgente',
  ANNOUNCEMENT_EVENT: 'Comunicado de evento',
  EVENT_REMINDER: 'Recordatorio de evento',
  VACCINE_EXPIRING: 'Vacuna por vencer',
  VACCINE_EXPIRED: 'Vacuna vencida',
  PET_LOST_REPORTED: 'Mascota reportada como perdida',
  PET_FOUND: 'Mascota encontrada',
  PET_REGISTERED: 'Mascota registrada',
  CHARGE_CANCELLED: 'Cargo cancelado',
  RESTRICTION_APPLIED: 'Restricción aplicada',
  RESTRICTION_RESOLVED: 'Restricción resuelta',
  UNIT_MEMBER_ASSIGNED: 'Miembro asignado a unidad',
  UNIT_MEMBER_REMOVED: 'Miembro removido de unidad',
  HOUSEHOLD_MEMBER_ADDED: 'Familiar agregado',
  HOUSEHOLD_MEMBER_REMOVED: 'Familiar removido',
  VEHICLE_REGISTERED: 'Vehículo registrado',
  VEHICLE_DEACTIVATED: 'Vehículo desactivado',
  AMENITY_CREATED: 'Amenidad creada',
  AMENITY_MAINTENANCE_SCHEDULED: 'Mantenimiento de amenidad programado',
  DOCUMENT_UPLOADED: 'Documento subido',
  POLL_RESULTS_PUBLISHED: 'Resultados de encuesta publicados',
  POLL_ABOUT_TO_CLOSE: 'Encuesta por cerrar',
  USER_ROLE_CHANGED: 'Rol de usuario cambiado',
  USER_ACCOUNT_DEACTIVATED: 'Cuenta de usuario desactivada',
  GDPR_EXPORT_READY: 'Exportación GDPR lista',
  CUSTOM: 'Personalizada',
};

function formatTypeLabel(type: NotificationType): string {
  return TYPE_LABELS[type] ?? type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Right Drawer
// ---------------------------------------------------------------------------

function NotificationConfigDrawer({ type, config, canUpdate, onApply, onClose }: {
  type: NotificationType;
  config: NotificationConfig | undefined;
  canUpdate: boolean;
  onApply: (type: NotificationType, patch: Partial<NotificationConfig>) => void;
  onClose: () => void;
}) {
  const [cadence, setCadence] = useState<NotificationCadence>(config?.cadence ?? { mode: 'immediate' });
  const [priority, setPriority] = useState<NotificationPriority>(config?.priority ?? 'NORMAL');
  const [quietHours, setQuietHours] = useState<QuietHours>(
    config?.quietHours ?? { enabled: false, startTime: '22:00', endTime: '08:00' }
  );

  const handleApply = () => { if (!canUpdate) return; onApply(type, { cadence, priority, quietHours }); onClose(); };

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} aria-hidden="true" />
      <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-xl z-50 flex flex-col overflow-hidden" role="dialog" aria-modal="true">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900 truncate">{formatTypeLabel(type)}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 rounded p-1 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Cerrar">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          {/* Cadence */}
          <section>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Frecuencia de envío</h3>
            <div className="space-y-2">
              {(['immediate', 'digest', 'weekly'] as const).map((mode) => (
                <div key={mode}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name={`cadence-${type}`} checked={cadence.mode === mode}
                      disabled={!canUpdate}
                      onChange={() => setCadence(mode === 'immediate' ? { mode } : mode === 'digest' ? { mode, time: '08:00' } : { mode, day: 1, time: '08:00' })}
                      className="text-blue-600 focus:ring-blue-500" />
                    <span className="text-sm text-gray-700">
                      {{ immediate: 'Inmediato', digest: 'Resumen diario', weekly: 'Semanal' }[mode]}
                    </span>
                  </label>
                  {cadence.mode === 'digest' && mode === 'digest' && (
                    <div className="ml-6 mt-1">
                      <input type="time" value={(cadence as Extract<NotificationCadence, { mode: 'digest' }>).time}
                        disabled={!canUpdate}
                        onChange={(e) => setCadence({ mode: 'digest', time: e.target.value })}
                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  )}
                  {cadence.mode === 'weekly' && mode === 'weekly' && (
                    <div className="ml-6 mt-1 space-y-2">
                      <select value={(cadence as Extract<NotificationCadence, { mode: 'weekly' }>).day}
                        disabled={!canUpdate}
                        onChange={(e) => setCadence({ mode: 'weekly', day: Number(e.target.value) as 0|1|2|3|4|5|6, time: (cadence as Extract<NotificationCadence, { mode: 'weekly' }>).time })}
                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full">
                        {DAYS_OF_WEEK.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </select>
                      <input type="time" value={(cadence as Extract<NotificationCadence, { mode: 'weekly' }>).time}
                        disabled={!canUpdate}
                        onChange={(e) => setCadence({ mode: 'weekly', day: (cadence as Extract<NotificationCadence, { mode: 'weekly' }>).day, time: e.target.value })}
                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
          {/* Priority */}
          <section>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Prioridad</h3>
            <select value={priority} onChange={(e) => setPriority(e.target.value as NotificationPriority)}
              disabled={!canUpdate}
              className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full">
              {(Object.entries(PRIORITY_LABELS) as [NotificationPriority, string][]).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </section>
          {/* Quiet hours */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">Horas de silencio</h3>
              <Switch checked={quietHours.enabled} onChange={(v) => setQuietHours((q) => ({ ...q, enabled: v }))} disabled={!canUpdate} />
            </div>
            {quietHours.enabled && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Inicio</label>
                    <input type="time" value={quietHours.startTime} disabled={!canUpdate} onChange={(e) => setQuietHours((q) => ({ ...q, startTime: e.target.value }))} className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Fin</label>
                    <input type="time" value={quietHours.endTime} disabled={!canUpdate} onChange={(e) => setQuietHours((q) => ({ ...q, endTime: e.target.value }))} className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full" />
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200 bg-gray-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">Cancelar</button>
          {canUpdate && (
            <button type="button" onClick={handleApply} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">Aplicar</button>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Table row
// ---------------------------------------------------------------------------

function NotificationTypeRow({ type, config, dirty, canUpdate, onToggle, onChannelToggle, onOpenDrawer }: {
  type: NotificationType;
  config: NotificationConfig | undefined;
  dirty: Partial<NotificationConfig> | undefined;
  canUpdate: boolean;
  onToggle: (type: NotificationType, enabled: boolean) => void;
  onChannelToggle: (type: NotificationType, ch: NotificationChannel, checked: boolean) => void;
  onOpenDrawer: (type: NotificationType) => void;
}) {
  const merged = { ...config, ...dirty };
  const enabled = merged.enabled ?? true;
  const channels: NotificationChannel[] = merged.channels ?? ['IN_APP'];
  const showError = enabled && channels.length === 0;

  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
      <td className="py-3 pl-4 pr-3 text-sm text-gray-700 min-w-[180px]">{formatTypeLabel(type)}</td>
      <td className="py-3 px-3 text-center">
        <Switch checked={enabled} onChange={(v) => onToggle(type, v)} ariaLabel={`Activar ${formatTypeLabel(type)}`} disabled={!canUpdate} />
      </td>
      <td className="py-3 px-3">
        <div className="flex flex-wrap gap-3">
          {DISPLAY_CHANNELS.map((ch) => (
            <label key={ch} className="flex items-center gap-1 cursor-pointer select-none">
              <input type="checkbox" checked={channels.includes(ch)} disabled={!enabled || !canUpdate}
                onChange={(e) => onChannelToggle(type, ch, e.target.checked)}
                className="rounded text-blue-600 focus:ring-blue-500 disabled:opacity-40" />
              <span className={`text-xs ${enabled ? 'text-gray-600' : 'text-gray-400'}`}>{CHANNEL_LABELS[ch]}</span>
            </label>
          ))}
        </div>
        {showError && <p className="text-xs text-red-500 mt-1">Selecciona al menos un canal.</p>}
      </td>
      {canUpdate && (
        <td className="py-3 pl-3 pr-4 text-right">
          <button type="button" onClick={() => onOpenDrawer(type)} className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1">
            Config
          </button>
        </td>
      )}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Accordion section
// ---------------------------------------------------------------------------

function CategorySection({ category, types, configs, dirtyMap, isExpanded, canUpdate, onToggleExpand, onTypeToggle, onChannelToggle, onOpenDrawer }: {
  category: NotificationCategory; types: NotificationType[]; configs: NotificationConfig[];
  dirtyMap: Map<NotificationType, Partial<NotificationConfig>>; isExpanded: boolean;
  canUpdate: boolean;
  onToggleExpand: (cat: NotificationCategory) => void;
  onTypeToggle: (type: NotificationType, enabled: boolean) => void;
  onChannelToggle: (type: NotificationType, ch: NotificationChannel, checked: boolean) => void;
  onOpenDrawer: (type: NotificationType) => void;
}) {
  const meta = CATEGORY_META[category];
  const activeCount = types.filter((t) => {
    const cfg = configs.find((c) => c.type === t);
    const d = dirtyMap.get(t);
    return (d?.enabled ?? cfg?.enabled) !== false;
  }).length;
  const allChannels = new Set<NotificationChannel>();
  types.forEach((t) => {
    const cfg = configs.find((c) => c.type === t);
    const d = dirtyMap.get(t);
    (d?.channels ?? cfg?.channels ?? ['IN_APP']).forEach((ch) => allChannels.add(ch));
  });

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button type="button" onClick={() => onToggleExpand(category)} aria-expanded={isExpanded}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
        <div className="flex items-center gap-3">
          <meta.icon size={18} className="text-slate-500 shrink-0" aria-hidden="true" />
          <span className="text-sm font-medium text-gray-800">{meta.label}</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            {activeCount}/{types.length} activos
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!isExpanded && (
            <div className="flex gap-1">
              {Array.from(allChannels).slice(0, 3).map((ch) => (
                <span key={ch} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-200 text-gray-600">{CHANNEL_LABELS[ch]}</span>
              ))}
            </div>
          )}
          <span className="text-gray-400 text-xs" aria-hidden="true">{isExpanded ? '▲' : '▼'}</span>
        </div>
      </button>
      {isExpanded && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead>
              <tr className="bg-white">
                <th className="py-2 pl-4 pr-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notificación</th>
                <th className="py-2 px-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Activo</th>
                <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Canales</th>
                {canUpdate && (
                  <th className="py-2 pl-3 pr-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avanzado</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white">
              {types.map((type) => (
                <NotificationTypeRow key={type} type={type}
                  config={configs.find((c) => c.type === type)} dirty={dirtyMap.get(type)}
                  canUpdate={canUpdate} onToggle={onTypeToggle} onChannelToggle={onChannelToggle} onOpenDrawer={onOpenDrawer} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AdminNotificationConfigList() {
  const { notificationConfigRepo, systemSettings } = useServices();
  const { confirm } = useConfirmation();
  const ability = useAbility();
  const canUpdateNotificationConfig = ability.can('manage', 'all')
    || ability.can('manage', 'NotificationConfig')
    || ability.can('update', 'NotificationConfig');
  const [configs, setConfigs] = useState<NotificationConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [dirty, setDirty] = useState<Map<NotificationType, Partial<NotificationConfig>>>(new Map());
  const [drawerType, setDrawerType] = useState<NotificationType | null>(null);
  const [expanded, setExpanded] = useState<Set<NotificationCategory>>(new Set());
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const loadConfigs = useCallback(async () => {
    setLoading(true); setFetchError(null);
    try { setConfigs(await notificationConfigRepo.list()); }
    catch { setFetchError('No se pudieron cargar las configuraciones. Intenta de nuevo.'); }
    finally { setLoading(false); }
  }, [notificationConfigRepo]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const handleTypeToggle = useCallback((type: NotificationType, enabled: boolean) => {
    if (!canUpdateNotificationConfig) return;
    setDirty((prev) => new Map(prev).set(type, { ...(prev.get(type) ?? {}), enabled }));
  }, [canUpdateNotificationConfig]);

  const handleChannelToggle = useCallback((type: NotificationType, ch: NotificationChannel, checked: boolean) => {
    if (!canUpdateNotificationConfig) return;
    setDirty((prev) => {
      const next = new Map(prev);
      const existing = next.get(type) ?? {};
      const base: NotificationChannel[] = existing.channels ?? configs.find((c) => c.type === type)?.channels ?? ['IN_APP'];
      const channels: NotificationChannel[] = checked ? [...new Set([...base, ch])] : base.filter((x) => x !== ch);
      return next.set(type, { ...existing, channels });
    });
  }, [canUpdateNotificationConfig, configs]);

  const handleDrawerApply = useCallback((type: NotificationType, patch: Partial<NotificationConfig>) => {
    if (!canUpdateNotificationConfig) return;
    setDirty((prev) => new Map(prev).set(type, { ...(prev.get(type) ?? {}), ...patch }));
  }, [canUpdateNotificationConfig]);

  const handleToggleExpand = useCallback((cat: NotificationCategory) => {
    setExpanded((prev) => { const next = new Set(prev); next.has(cat) ? next.delete(cat) : next.add(cat); return next; });
  }, []);

  const handleSave = async () => {
    if (!canUpdateNotificationConfig) return;
    if (dirty.size === 0) return;
    for (const [type, patch] of dirty.entries()) {
      const merged = { ...configs.find((c) => c.type === type), ...patch };
      if (merged.enabled && (merged.channels ?? []).length === 0) {
        setFeedback({ msg: `"${formatTypeLabel(type)}" está activo pero sin canales seleccionados.`, type: 'error' }); return;
      }
    }
    setSaving(true); setFeedback(null);
    try {
      const tz = systemSettings?.timezone ?? 'America/Mexico_City';
      const updates = Array.from(dirty.entries()).map(([type, patch]) => {
        const update = { type, ...patch };
        if (update.quietHours) update.quietHours = { ...update.quietHours, timezone: tz };
        return update;
      });
      await notificationConfigRepo.bulkUpdate(updates as Parameters<typeof notificationConfigRepo.bulkUpdate>[0]);
      await loadConfigs(); setDirty(new Map());
      setFeedback({ msg: 'Configuraciones guardadas correctamente.', type: 'success' });
    } catch {
      setFeedback({ msg: 'Error al guardar los cambios. Intenta de nuevo.', type: 'error' });
    } finally { setSaving(false); }
  };

  const handleResetDefaults = async () => {
    if (!canUpdateNotificationConfig) return;
    const confirmed = await confirm({
      title: '¿Restablecer configuraciones?',
      message: 'Se restablecerán todas las configuraciones a los valores predeterminados.',
      confirmText: 'Restablecer',
      cancelText: 'Cancelar',
      variant: 'warning',
    });
    if (!confirmed) return;
    setResetting(true); setFeedback(null);
    try {
      await notificationConfigRepo.resetToDefaults(); await loadConfigs(); setDirty(new Map());
      setFeedback({ msg: 'Configuraciones restablecidas a los valores predeterminados.', type: 'success' });
    } catch {
      setFeedback({ msg: 'Error al restablecer. Intenta de nuevo.', type: 'error' });
    } finally { setResetting(false); }
  };

  const categories = Object.keys(CATEGORY_META) as NotificationCategory[];

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Configuración de Notificaciones</h1>
          <p className="text-sm text-gray-500 mt-1">Gestiona qué notificaciones se envían, por qué canales y con qué frecuencia.</p>
        </div>
        {canUpdateNotificationConfig && (
          <div className="flex items-center gap-3 flex-shrink-0">
            <button type="button" onClick={handleResetDefaults} disabled={resetting || loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {resetting ? 'Restableciendo...' : 'Restablecer predeterminados'}
            </button>
            <button type="button" onClick={handleSave} disabled={dirty.size === 0 || saving || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {saving ? 'Guardando...' : `Guardar cambios${dirty.size > 0 ? ` (${dirty.size})` : ''}`}
            </button>
          </div>
        )}
      </div>

      {feedback && (
        <div className={`flex items-center justify-between rounded-md px-4 py-3 text-sm ${feedback.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`} role="alert">
          <span>{feedback.msg}</span>
          <button type="button" onClick={() => setFeedback(null)} className="ml-4 font-medium underline focus:outline-none">OK</button>
        </div>
      )}

      {loading && (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="border border-gray-200 rounded-lg overflow-hidden animate-pulse">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-gray-200 rounded" />
                  <div className="w-32 h-4 bg-gray-200 rounded" />
                  <div className="w-16 h-4 bg-gray-200 rounded-full" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {fetchError && !loading && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4">
          <p className="text-sm text-red-700">{fetchError}</p>
          <button type="button" onClick={loadConfigs} className="mt-2 text-sm font-medium text-red-600 hover:text-red-800 underline focus:outline-none">Reintentar</button>
        </div>
      )}

      {!loading && !fetchError && (
        <div className="space-y-3">
          {categories.map((category) => {
            const types = CATEGORY_TO_TYPES[category] ?? [];
            if (types.length === 0) return null;
            return (
              <CategorySection key={category} category={category} types={types} configs={configs}
                dirtyMap={dirty} isExpanded={expanded.has(category)} canUpdate={canUpdateNotificationConfig} onToggleExpand={handleToggleExpand}
                onTypeToggle={handleTypeToggle} onChannelToggle={handleChannelToggle}
                onOpenDrawer={(type) => setDrawerType(type)} />
            );
          })}
        </div>
      )}

      {drawerType !== null && (
        <NotificationConfigDrawer type={drawerType} config={configs.find((c) => c.type === drawerType)}
          canUpdate={canUpdateNotificationConfig} onApply={handleDrawerApply} onClose={() => setDrawerType(null)} />
      )}
    </div>
  );
}

export default AdminNotificationConfigList;
